import BaseScreen from 'components/base-screen';
import { createContext, memo, useEffect, useRef, useState } from 'react';
import HeaderEstimate from './components/header/headerEstimate';
import FormEstimateMarketPrice from './components/forms/formMarketPrice/formEstimateMarketPrice';
import AboutObjectEstimate from './components/aboutObject/aboutObjectEstimate';
import PossibilitiesEstimate from './components/possibilities/posibilitiesEstimate';
import { getTransformedMinMaxMarketPrice, toReadablePrice } from 'lib/utils';
import { IPrimaryFacilityEstimateResponse, ISecondaryFacilityEstimateResponse } from './interfaces';
import AboutObjectEstimateInSystem from './components/aboutObject/aboutObjectEstimateInSystem';
import { AboutObjectEstimateButtonRecommendations } from './components/aboutObject/aboutObjectEstimateButtonAnalog';
import { MarketTypes } from 'api/types';

import style from './styles.module.scss';
import clsx from 'clsx';
import useBoundStore from 'store';

export interface ISecondaryFacilityEstimateResponseWithAddress
  extends ISecondaryFacilityEstimateResponse {
  address: string;
}
export interface IPrimaryFacilityEstimateResponseWithAddress
  extends IPrimaryFacilityEstimateResponse {
  address: string;
}
export interface IDataModal {
  areaInSqM: string;
  typeApartment?: string;
  typeApartmentId?: number;
  address?: string;
  buildingId: number;
  complexId?: number;
  streetId?: number;
  apartmentNumber?: string;
  estimatedPrice?: number;
}
export interface IDataModalInSystem {
  facilityId: number;
  marketType: MarketTypes;
}
interface IDataEstimateContext {
  dataModal: IDataModal | null;
  dataModalInSystem: IDataModalInSystem | null;
  onSetDataModal: (data: Partial<IDataModal> | null) => void;
}

export const DataEstimateContext = createContext<IDataEstimateContext | null>(null);

interface IEstimateProps {
  marketType?: MarketTypes;
  dataById?:
    | ISecondaryFacilityEstimateResponseWithAddress
    | IPrimaryFacilityEstimateResponseWithAddress;
  facilityId?: number;
}

const Estimate = ({ dataById, marketType, facilityId }: IEstimateProps) => {
  const isModeById = Boolean(facilityId);

  const [data, setData] = useState<
    | ISecondaryFacilityEstimateResponseWithAddress
    | IPrimaryFacilityEstimateResponseWithAddress
    | null
  >(null);

  const isHideElement = isModeById;
  const isBlockResetOnMount = isModeById;

  useEffect(() => {
    if (dataById) {
      setData(() => dataById);
    }
  }, [dataById]);

  const handleSetData = (
    data:
      | ISecondaryFacilityEstimateResponseWithAddress
      | IPrimaryFacilityEstimateResponseWithAddress,
  ) => {
    setData(data);
  };

  const handleResetData = () => {
    setData(null);
  };

  const blockAppearsRef = useRef<null | HTMLDivElement>(null);
  const scrollToBottom = () => {
    const yOffset = -100;
    if (blockAppearsRef.current) {
      const y = blockAppearsRef.current?.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [data]);

  const [isSecondarySwitcher, setSwitcher] = useState(true);
  const handleSwitch = (value: boolean) => {
    setSwitcher(value);
  };

  useEffect(() => {
    if (!isBlockResetOnMount) {
      handleResetData();
    }
  }, [isSecondarySwitcher]);

  const getYear = () => {
    if (data) {
      if ('commissioningYear' in data) {
        return data.commissioningYear;
      } else {
        if (data.commissioningDate) {
          return data.commissioningDate;
        } else {
          return data.commissioningQuarter;
        }
      }
    }
  };

  const getImages = () => {
    if (data && 'plans' in data) {
      return data.plans.map((plan) => ({ src: plan.url, alt: plan.name }));
    }
  };

  const [dataModal, setDataModal] = useState<IDataModal | null>(null);
  const handleSetDataModal = (data: Partial<IDataModal> | null) => {
    setDataModal(data as IDataModal | null);
  };

  const [dataModalInSystem, setDataModalInSystem] = useState<IDataModalInSystem | null>(null);

  useEffect(() => {
    if (data?.averageMarketPrice) {
      setDataModal((prev) => ({
        areaInSqM: prev?.areaInSqM as string,
        typeApartment: prev?.apartmentNumber,
        typeApartmentId: prev?.typeApartmentId,
        address: prev?.address,
        buildingId: prev?.buildingId as number,
        complexId: prev?.complexId,
        streetId: prev?.streetId,
        apartmentNumber: prev?.apartmentNumber,
        estimatedPrice: data.averageMarketPrice,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (facilityId && marketType) {
      setDataModalInSystem({ facilityId, marketType });
    }
  }, [facilityId]);

  const user = useBoundStore((s) => s.user);
  const isClient = user?.type === 'Client';

  const getEstimate = () => {
    return (
      <section className={style.estimate}>
        <DataEstimateContext.Provider
          value={{
            dataModal,
            dataModalInSystem,
            onSetDataModal: handleSetDataModal,
          }}
        >
          <div
            className={clsx(
              style.estimate__container,
              isModeById && style.estimate__container_none,
            )}
          >
            {!isHideElement && (
              <HeaderEstimate switcher={isSecondarySwitcher} onSwitch={handleSwitch} />
            )}
            {!isHideElement && (
              <FormEstimateMarketPrice
                mode='default'
                isSecondarySwitcher={isSecondarySwitcher}
                onCallBackData={handleSetData}
                resetData={handleResetData}
              />
            )}
            {data && (
              <div ref={blockAppearsRef}>
                {isModeById ? (
                  <AboutObjectEstimateInSystem
                    maxFacilityPrice={getTransformedMinMaxMarketPrice(
                      String(data.maxFacilityPrice),
                    )}
                    minFacilityPrice={getTransformedMinMaxMarketPrice(
                      String(data.minFacilityPrice),
                    )}
                    marketPrice={toReadablePrice(data.averageMarketPrice)}
                  />
                ) : (
                  <AboutObjectEstimate
                    isSecondary={isSecondarySwitcher}
                    maxFacilityPrice={getTransformedMinMaxMarketPrice(
                      String(data.maxFacilityPrice),
                    )}
                    minFacilityPrice={getTransformedMinMaxMarketPrice(
                      String(data.minFacilityPrice),
                    )}
                    marketPrice={toReadablePrice(data.averageMarketPrice)}
                    floorCount={data.floorCount}
                    wallMaterial={data.wallMaterial}
                    commissioningYear={String(getYear()) + ' г'}
                    recommendations={data.recommendations}
                    sameFacilityCount={data.sameFacilityCount}
                    address={data.address}
                    images={getImages()}
                  />
                )}
                {isClient && (
                  <PossibilitiesEstimate
                    isHideBorder={isModeById}
                    isMarginMore={isModeById}
                    averageMarketPrice={toReadablePrice(data.averageMarketPrice)}
                    mortgageInfo={data.mortgageInfo}
                    isSecondary={isSecondarySwitcher}
                  />
                )}
                {isModeById && (
                  <div>
                    <AboutObjectEstimateButtonRecommendations
                      recommendations={data.recommendations}
                      isSecondary={marketType === MarketTypes.SECONDARY}
                      sameFacilityCount={data.sameFacilityCount}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          {!isHideElement && (
            <div className={style.estimate__disclaimer}>
              Расчет проводится автоматически на основе находящихся в продаже объектов. И может быть
              уточнен по запросу сотрудником ООО "Реит". Не оферта.
            </div>
          )}
        </DataEstimateContext.Provider>
      </section>
    );
  };

  if (isModeById) {
    return getEstimate();
  }

  return <BaseScreen title='Оценка недвижимости'>{getEstimate()}</BaseScreen>;
};

export default memo(Estimate, (prev, current) => prev.facilityId === current.facilityId);
